import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { getQuizMonitor, startQuizTimer } from '../api/api';

const fmt = (s) => {
    s = Math.max(0, Math.round(s));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

const relTime = (ts) => {
    if (!ts) return '—';
    const diff = Math.round((Date.now() / 1000) - ts);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
};

export default function QuizMonitorPage() {
    const { quizId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [starting, setStarting] = useState(false);
    const [now, setNow] = useState(Date.now() / 1000);
    const pollRef = useRef();

    const load = useCallback(async () => {
        try {
            const d = await getQuizMonitor(quizId);
            setData(d);
            setError('');
        } catch (err) {
            setError(err?.response?.data?.detail || 'Failed to load quiz data.');
        }
    }, [quizId]);

    useEffect(() => {
        load();
        pollRef.current = setInterval(() => {
            load();
            setNow(Date.now() / 1000);
        }, 4000);
        const tick = setInterval(() => setNow(Date.now() / 1000), 1000);
        return () => { clearInterval(pollRef.current); clearInterval(tick); };
    }, [load]);

    const handleStartTimer = async () => {
        setStarting(true);
        try {
            await startQuizTimer(quizId);
            await load();
        } catch (err) {
            alert(err?.response?.data?.detail || 'Failed to start timer.');
        } finally { setStarting(false); }
    };

    // Derived timer state
    const timerStarted = data?.timer_started_at != null;
    const timerMinutes = data?.timer_minutes || 0;
    const remainingSecs = timerStarted
        ? Math.max(0, timerMinutes * 60 - (now - data.timer_started_at))
        : timerMinutes * 60;
    const timerExpired = timerStarted && remainingSecs <= 0;

    if (error) return (
        <div style={styles.center}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
            <h2 style={{ color: '#dc2626' }}>Error</h2>
            <p style={{ color: '#6b7280' }}>{error}</p>
            <button style={styles.btnRed} onClick={load}>Retry</button>
        </div>
    );

    if (!data) return (
        <div style={styles.center}>
            <div style={styles.spinner} />
            <p style={{ color: '#6b7280', marginTop: 12 }}>Loading quiz monitor…</p>
        </div>
    );

    const shareUrl = `${window.location.origin}/quiz/${quizId}`;
    const participants = data.participants || [];
    const inProgress = participants.filter(p => p.status === 'in_progress');
    const completed = participants.filter(p => p.status === 'completed');

    return (
        <div style={styles.page}>
            {/* Header */}
            <div style={styles.header}>
                <div>
                    <div style={styles.brand}>{data.tenant_name || 'AcademicOS'} · Quiz Monitor</div>
                    <div style={styles.title}>{data.title}</div>
                    <div style={styles.topic}>{data.topic}</div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {data.passcode && (
                        <span style={styles.badge}>🔒 {data.passcode}</span>
                    )}
                    <a href={shareUrl} target="_blank" rel="noreferrer" style={styles.btnOutline}>
                        Open Quiz ↗
                    </a>
                    <CopyButton text={shareUrl} />
                    <div style={styles.liveTag}>● LIVE</div>
                </div>
            </div>

            <div style={styles.body}>
                {/* Stats row */}
                <div style={styles.statsRow}>
                    <StatCard label="Total Joined" value={data.participant_count} icon="👥" />
                    <StatCard label="In Progress" value={inProgress.length} icon="⏳" highlight={inProgress.length > 0} />
                    <StatCard label="Completed" value={data.completed_count ?? completed.length} icon="✅" />
                    <StatCard label="Avg Score" value={completed.length ? `${data.avg_pct}%` : '—'} icon="📊" />
                </div>

                <div style={styles.mainGrid}>
                    {/* Timer panel */}
                    <div style={styles.card}>
                        <div style={styles.cardTitle}>⏱ Timer Control</div>
                        {timerMinutes === 0 ? (
                            <p style={{ color: '#6b7280', fontSize: 13 }}>No timer set for this quiz.</p>
                        ) : (
                            <>
                                <div style={styles.timerDisplay(timerExpired)}>
                                    {timerExpired ? 'Time Up' : fmt(remainingSecs)}
                                </div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16, textAlign: 'center' }}>
                                    {timerMinutes}-minute quiz
                                    {timerStarted && !timerExpired && <> · started {relTime(data.timer_started_at)}</>}
                                    {timerExpired && ' · quiz ended'}
                                </div>
                                {!timerStarted ? (
                                    <div>
                                        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>
                                            Students on the quiz page see "Waiting…" until you start the timer.
                                            All connected students will start simultaneously.
                                        </p>
                                        <button
                                            style={{ ...styles.btnRed, width: '100%', fontSize: 15, padding: '12px 0' }}
                                            onClick={handleStartTimer}
                                            disabled={starting}
                                        >
                                            {starting ? 'Starting…' : '▶ Start Timer for All Students'}
                                        </button>
                                    </div>
                                ) : timerExpired ? (
                                    <div style={{ ...styles.pill, background: '#fee2e2', color: '#dc2626' }}>
                                        Timer expired — quiz closed
                                    </div>
                                ) : (
                                    <div style={{ ...styles.pill, background: '#d1fae5', color: '#065f46' }}>
                                        ✓ Timer running · students are working
                                    </div>
                                )}
                            </>
                        )}
                    </div>

                    {/* Quiz link */}
                    <div style={styles.card}>
                        <div style={styles.cardTitle}>🔗 Share Link</div>
                        <div style={styles.linkBox}>
                            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                                {shareUrl}
                            </span>
                            <CopyButton text={shareUrl} small />
                        </div>
                        {data.passcode && (
                            <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                                Share passcode <strong style={{ color: '#dc2626' }}>{data.passcode}</strong> separately with students.
                            </div>
                        )}
                        <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af' }}>
                            Quiz expires in ~{fmt(data.expires_at - now)} · Created {relTime(data.created_at)}
                        </div>
                    </div>
                </div>

                {/* Participants table */}
                <div style={{ ...styles.card, marginTop: 16 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <div style={styles.cardTitle}>👥 Participants ({participants.length})</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>Auto-refreshes every 4 s</div>
                    </div>
                    {participants.length === 0 ? (
                        <div style={styles.empty}>
                            <div style={{ fontSize: 32, marginBottom: 8 }}>🎓</div>
                            <div>No participants yet. Share the quiz link with students.</div>
                        </div>
                    ) : (
                        <div style={{ overflowX: 'auto' }}>
                            <table style={styles.table}>
                                <thead>
                                    <tr style={styles.thead}>
                                        <th style={styles.th}>#</th>
                                        <th style={styles.th}>Name</th>
                                        <th style={styles.th}>Status</th>
                                        <th style={styles.th}>Score</th>
                                        <th style={styles.th}>Percentage</th>
                                        <th style={styles.th}>Time</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants.map((p, i) => {
                                        const done = p.status === 'completed';
                                        const rank = completed.findIndex(c => c.name === p.name && c.joined_at === p.joined_at);
                                        return (
                                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                                <td style={{ ...styles.td, fontWeight: 700, color: done && rank < 3 ? '#dc2626' : '#9ca3af' }}>
                                                    {done ? (rank === 0 ? '🥇' : rank === 1 ? '🥈' : rank === 2 ? '🥉' : rank + 1) : '—'}
                                                </td>
                                                <td style={{ ...styles.td, fontWeight: 600 }}>{p.name}</td>
                                                <td style={styles.td}>
                                                    {done
                                                        ? <span style={styles.statusDone}>✓ Completed</span>
                                                        : <span style={styles.statusActive}>⏳ In Progress</span>
                                                    }
                                                </td>
                                                <td style={styles.td}>
                                                    {done ? `${p.score} / ${p.total}` : <span style={{ color: '#d1d5db' }}>—</span>}
                                                </td>
                                                <td style={styles.td}>
                                                    {done ? (
                                                        <div style={styles.scoreBar}>
                                                            <div style={styles.scoreBarFill(p.pct)} />
                                                            <span style={{ fontSize: 12, fontWeight: 700, color: pctColor(p.pct) }}>
                                                                {p.pct}%
                                                            </span>
                                                        </div>
                                                    ) : <span style={{ color: '#d1d5db' }}>—</span>}
                                                </td>
                                                <td style={{ ...styles.td, color: '#9ca3af', fontSize: 12 }}>
                                                    {done ? relTime(p.submitted_at) : `joined ${relTime(p.joined_at)}`}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon, highlight }) {
    return (
        <div style={{ ...styles.statCard, borderColor: highlight ? '#fcd34d' : '#e5e7eb', background: highlight ? '#fffbeb' : '#fff' }}>
            <div style={{ fontSize: 24, marginBottom: 4 }}>{icon}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: highlight ? '#92400e' : '#111827' }}>{value}</div>
            <div style={{ fontSize: 12, color: highlight ? '#92400e' : '#6b7280', fontWeight: 600 }}>{label}</div>
        </div>
    );
}

function CopyButton({ text, small }) {
    const [copied, setCopied] = useState(false);
    const click = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };
    return (
        <button
            onClick={click}
            style={{ ...styles.btnOutline, fontSize: small ? 11 : 12, padding: small ? '4px 8px' : '6px 14px' }}
        >
            {copied ? '✓ Copied' : 'Copy'}
        </button>
    );
}

const pctColor = (pct) => pct >= 80 ? '#065f46' : pct >= 60 ? '#92400e' : '#991b1b';

const styles = {
    page: {
        minHeight: '100vh', background: '#f9fafb', fontFamily: "'Segoe UI', system-ui, sans-serif",
    },
    header: {
        background: '#fff', borderBottom: '3px solid #dc2626', padding: '16px 32px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        boxShadow: '0 2px 8px rgba(0,0,0,.07)',
    },
    brand: { fontSize: 11, fontWeight: 700, color: '#dc2626', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 2 },
    title: { fontSize: 18, fontWeight: 800, color: '#111827' },
    topic: { fontSize: 12, color: '#6b7280', marginTop: 2 },
    badge: {
        background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d',
        padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
    },
    liveTag: {
        background: '#dcfce7', color: '#166534', padding: '4px 10px',
        borderRadius: 20, fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
        animation: 'pulse 2s infinite',
    },
    body: { maxWidth: 1100, margin: '0 auto', padding: '24px 16px' },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 },
    statCard: {
        background: '#fff', borderRadius: 12, padding: '20px 16px', textAlign: 'center',
        border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,.05)',
    },
    mainGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
    card: {
        background: '#fff', borderRadius: 12, padding: 20,
        border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,.05)',
    },
    cardTitle: { fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14, textTransform: 'uppercase', letterSpacing: 0.5 },
    timerDisplay: (expired) => ({
        fontSize: 48, fontWeight: 900, textAlign: 'center', marginBottom: 8,
        color: expired ? '#9ca3af' : '#dc2626', fontVariantNumeric: 'tabular-nums',
    }),
    pill: {
        padding: '10px 16px', borderRadius: 8, textAlign: 'center',
        fontSize: 13, fontWeight: 600,
    },
    linkBox: {
        display: 'flex', alignItems: 'center', gap: 8, background: '#f9fafb',
        border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 12px',
    },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
    thead: { borderBottom: '2px solid #e5e7eb' },
    th: { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 },
    td: { padding: '12px 12px', borderBottom: '1px solid #f3f4f6' },
    statusDone: {
        background: '#d1fae5', color: '#065f46', padding: '2px 8px',
        borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    },
    statusActive: {
        background: '#fef3c7', color: '#92400e', padding: '2px 8px',
        borderRadius: 12, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    },
    scoreBar: { display: 'flex', alignItems: 'center', gap: 8 },
    scoreBarFill: (pct) => ({
        height: 8, width: `${pct}%`, maxWidth: 120, borderRadius: 4,
        background: pct >= 80 ? '#16a34a' : pct >= 60 ? '#d97706' : '#dc2626',
    }),
    empty: { textAlign: 'center', padding: '32px 0', color: '#9ca3af', fontSize: 13 },
    center: {
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', height: '100vh', fontFamily: 'system-ui',
    },
    spinner: {
        width: 36, height: 36, border: '3px solid #dc2626',
        borderTopColor: 'transparent', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    btnRed: {
        background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8,
        padding: '8px 20px', fontWeight: 700, cursor: 'pointer', fontSize: 13,
        transition: 'background .15s',
    },
    btnOutline: {
        background: 'transparent', color: '#374151', border: '1px solid #e5e7eb',
        borderRadius: 8, padding: '6px 14px', fontWeight: 600, cursor: 'pointer',
        fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center',
    },
};
